import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './api.js'

/**
 * 「自分のテナント」情報の取得 API + フック。
 *
 * UI のプラン別出し分け（PRO限定機能の表示制御、容量バー等）に使う。
 * - 認証済（Supabase JWT）でしか叩かない前提
 * - 暫定テナント / admin は使わない
 */

export type MeTenant = {
  id: string
  name: string | null
  kind: 'REGULAR' | 'EPHEMERAL'
  plan: 'FREE' | 'PRO' | 'EPHEMERAL'
  status: 'ACTIVE' | 'FROZEN' | 'SUSPENDED' | 'DELETED'
  storageUsedBytes: string
  addonStorageTb: number
  expiresAt: string | null
}

async function getMeTenant(): Promise<MeTenant> {
  return apiFetch<MeTenant>('/me/tenant')
}

/**
 * 自分のテナント情報を更新する（現状は name のみ）。
 *
 * - 暫定テナント (EPHEMERAL) は API 側で 403 になるので呼出側でも UI で無効化する想定
 * - 成功時に呼出側で `queryClient.invalidateQueries({ queryKey: ['me-tenant'] })` する
 */
export async function updateMeTenant(input: { name: string }): Promise<MeTenant> {
  return apiFetch<MeTenant>('/me/tenant', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/**
 * react-query フック版。enabled で 401 になりそうなケースを抑止する。
 */
export function useMeTenant(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['me-tenant'],
    queryFn: getMeTenant,
    enabled: options.enabled ?? true,
    // ヘッダ表示やプラン判定で参照される。テナント情報は頻繁には変わらないので長めにキャッシュして
    // 画面遷移ごとの再 fetch を抑える（5分）
    staleTime: 5 * 60 * 1000,
  })
}
